/**
 * Telnyx WebRTC Service
 *
 * Drop-in replacement for ringCentralService.js using TelnyxRTC.
 * Same callback API so useGlobalSoftphone.js and Softphone.jsx
 * work with minimal changes (just swap the import).
 *
 * Auth flow:
 *   1. GET /api/phone-token/ → bridge JWT + callerNumber + callerName
 *   2. POST planchetteonline.com/api/auth/virgo-token → session cookie
 *   3. POST planchetteonline.com/api/sip-token → TelnyxRTC login_token
 *   4. TelnyxRTC.connect() → SIP registration via wss://sip.telnyx.com
 */
import { TelnyxRTC } from '@telnyx/webrtc';
import api from './api';

const DEFAULT_CALLER_NUMBER = '';  // Set from firm settings
const DEFAULT_CALLER_NAME = '';  // Set from firm settings

class TelnyxService {
  constructor() {
    this.client = null;
    this.currentSession = null;
    this.isRegistered = false;
    this.callerNumber = DEFAULT_CALLER_NUMBER;
    this.callerName = DEFAULT_CALLER_NAME;
    this.callbacks = {
      onRegistered: null,
      onUnregistered: null,
      onRegistrationFailed: null,
      onIncomingCall: null,
      onCallAccepted: null,
      onCallEnded: null,
      onCallFailed: null,
      onCallHold: null,
      onCallUnhold: null,
      onCallMuted: null,
      onCallUnmuted: null,
      onConnectionStateChange: null,
    };
    this.connectionState = 'disconnected';
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  /**
   * Get a SIP login token via the Virgo → Planchette bridge.
   * Also receives the user's caller number and name.
   * Returns the JWT string for TelnyxRTC({ login_token }).
   */
  async _getSipToken() {
    const res = await api.get('/phone-token/');
    const token = res.data?.token;
    if (!token) throw new Error('No SIP token returned');
    if (res.data?.callerNumber) this.callerNumber = res.data.callerNumber;
    if (res.data?.callerName) this.callerName = res.data.callerName;
    return token;
  }

  // ── Status check ─────────────────────────────────────────────────────────

  async checkStatus() {
    try {
      const res = await api.get('/phone-token/');
      return {
        configured: true,
        ready: true,
        message: 'Telnyx ready',
      };
    } catch {
      return { configured: false, ready: false, message: 'Phone service unavailable' };
    }
  }

  // ── Initialize ───────────────────────────────────────────────────────────

  async initialize() {
    if (this.isRegistered && this.client) {
      console.log('[TelnyxService] Already registered, skipping initialize()');
      return true;
    }

    if (this._initializing) {
      console.log('[TelnyxService] Already initializing, skipping');
      return false;
    }
    this._initializing = true;

    try {
      this.setConnectionState('connecting');

      if (this.client) {
        console.log('[TelnyxService] Disposing existing client');
        try { this.client.disconnect(); } catch (e) { /* ignore */ }
        this.client = null;
        this.isRegistered = false;
        await new Promise((r) => setTimeout(r, 500));
      }

      const sipToken = await this._getSipToken();
      console.log('[TelnyxService] Got SIP token, connecting as', this.callerName, this.callerNumber);

      this.client = new TelnyxRTC({ login_token: sipToken });
      this._setupEventHandlers();
      this.client.connect();

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (this.isRegistered) { resolve(); return; }
          reject(new Error('SIP registration timed out after 20s'));
        }, 20000);

        const check = setInterval(() => {
          if (this.isRegistered) {
            clearInterval(check);
            clearTimeout(timeout);
            resolve();
          }
        }, 200);
      });

      console.log('[TelnyxService] SIP registered — phone is online');
      this._initializing = false;

      return true;
    } catch (error) {
      console.error('[TelnyxService] Initialize failed:', error);
      this.setConnectionState('error');
      this.callbacks.onRegistrationFailed?.(error);
      this._initializing = false;
      throw error;
    }
  }

  // ── Event handlers ───────────────────────────────────────────────────────

  _setupEventHandlers() {
    if (!this.client) return;

    this.client.on('telnyx.ready', () => {
      console.log('[TelnyxService] SIP registered');
      this.isRegistered = true;
      this.setConnectionState('registered');
      this.callbacks.onRegistered?.();
    });

    this.client.on('telnyx.error', (err) => {
      console.error('[TelnyxService] Error:', err);
      this.isRegistered = false;
      this.setConnectionState('error');
      this.callbacks.onRegistrationFailed?.(err);
    });

    this.client.on('telnyx.socket.close', () => {
      console.warn('[TelnyxService] WebSocket closed');
      this.isRegistered = false;
      this.setConnectionState('disconnected');
      this.callbacks.onUnregistered?.();
    });

    this.client.on('telnyx.notification', (notification) => {
      const call = notification.call;
      if (!call) return;

      switch (notification.type) {
        case 'callUpdate':
          this._handleCallUpdate(call);
          break;
        case 'newCall':
          if (call.direction === 'inbound') {
            this._handleIncomingCall(call);
          }
          break;
      }
    });
  }

  _handleIncomingCall(call) {
    console.log('[TelnyxService] Incoming call:', call.options);
    this.currentSession = call;
    this._setupCallHandlers(call);

    const callInfo = {
      id: call.id,
      from: call.options?.remoteCallerNumber || 'Unknown',
      fromName: call.options?.remoteCallerName || 'Unknown',
      from_number: call.options?.remoteCallerNumber || '',
      from_name: call.options?.remoteCallerName || '',
      direction: 'inbound',
    };

    this.callbacks.onIncomingCall?.(callInfo, call);
  }

  _handleCallUpdate(call) {
    const state = call.state;

    switch (state) {
      case 'ringing':
        if (call.direction === 'inbound' && !this.currentSession) {
          this._handleIncomingCall(call);
        }
        break;
      case 'early':
        break;
      case 'active':
        this.currentSession = call;
        this.callbacks.onCallAccepted?.(call);
        // Play AI monitoring announcement on outbound calls after answer
        if (call.direction === 'outbound' && call.id) {
          this._playAiAnnouncement(call.id);
        }
        break;
      case 'held':
        this.callbacks.onCallHold?.(call);
        break;
      case 'hangup':
      case 'destroy':
        this.currentSession = null;
        this.callbacks.onCallEnded?.(call);
        break;
    }
  }

  _setupCallHandlers(call) {
    // TelnyxRTC uses notification events, not per-session handlers.
  }

  // ── AI Announcement ────────────────────────────────────────────────────

  async _playAiAnnouncement(callControlId) {
    try {
      const { default: api } = await import('./api');
      await api.post('/telnyx/announce', { call_control_id: callControlId });
      console.log('[TelnyxService] AI monitoring announcement played');
    } catch (err) {
      console.warn('[TelnyxService] AI announcement failed:', err.message);
    }
  }

  // ── Call control ─────────────────────────────────────────────────────────

  async makeCall(phoneNumber, options = {}) {
    if (!this.client || !this.isRegistered) {
      throw new Error('Phone not registered. Please wait for connection.');
    }

    let formatted = phoneNumber.replace(/\D/g, '');
    if (formatted.length === 10) {
      formatted = '+1' + formatted;
    } else if (!formatted.startsWith('+')) {
      formatted = '+' + formatted;
    }

    console.log('[TelnyxService] Calling:', formatted);

    try {
      const call = this.client.newCall({
        destinationNumber: formatted,
        callerName: options.callerName || this.callerName,
        callerNumber: options.fromNumber || this.callerNumber,
        audio: true,
        video: false,
      });

      this.currentSession = call;

      return {
        id: call.id,
        phoneNumber: formatted,
        direction: 'outbound',
        session: call,
      };
    } catch (error) {
      console.error('[TelnyxService] Call failed:', error);
      throw error;
    }
  }

  async answerCall(session = null) {
    const call = session || this.currentSession;
    if (!call) throw new Error('No incoming call to answer');

    call.answer();
    return call;
  }

  async declineCall(session = null) {
    const call = session || this.currentSession;
    if (!call) throw new Error('No incoming call to decline');

    call.hangup();
    this.currentSession = null;
  }

  async hangup(session = null) {
    const call = session || this.currentSession;
    if (!call) {
      console.warn('[TelnyxService] hangup called but no session');
      return;
    }

    try {
      if (typeof call.hangup === 'function') {
        call.hangup();
      }
    } catch (err) {
      console.error('[TelnyxService] Hangup error:', err);
    }

    this.currentSession = null;
  }

  async hold(session = null) {
    const call = session || this.currentSession;
    if (!call) return;
    try { call.hold?.(); } catch (e) { console.error('[TelnyxService] Hold error:', e); }
  }

  async unhold(session = null) {
    const call = session || this.currentSession;
    if (!call) return;
    try { call.unhold?.(); } catch (e) { console.error('[TelnyxService] Unhold error:', e); }
  }

  mute(session = null) {
    const call = session || this.currentSession;
    if (!call) return;
    try {
      call.muteAudio?.();
      this.callbacks.onCallMuted?.(call);
    } catch (e) { console.error('[TelnyxService] Mute error:', e); }
  }

  unmute(session = null) {
    const call = session || this.currentSession;
    if (!call) return;
    try {
      call.unmuteAudio?.();
      this.callbacks.onCallUnmuted?.(call);
    } catch (e) { console.error('[TelnyxService] Unmute error:', e); }
  }

  sendDTMF(digit, session = null) {
    const call = session || this.currentSession;
    if (!call) return;
    try {
      call.dtmf?.(digit);
    } catch (e) { console.error('[TelnyxService] DTMF error:', e); }
  }

  async transfer(targetNumber, session = null) {
    const call = session || this.currentSession;
    if (!call) return;

    let formatted = targetNumber.replace(/\D/g, '');
    if (formatted.length === 10) formatted = '+1' + formatted;
    else if (!formatted.startsWith('+')) formatted = '+' + formatted;

    try {
      call.transfer?.(formatted);
    } catch (e) { console.error('[TelnyxService] Transfer error:', e); }

    this.currentSession = null;
  }

  // ── State ────────────────────────────────────────────────────────────────

  setConnectionState(state) {
    this.connectionState = state;
    this.callbacks.onConnectionStateChange?.(state);
  }

  getConnectionState() {
    return this.connectionState;
  }

  getIsRegistered() {
    return this.isRegistered;
  }

  getCurrentSession() {
    return this.currentSession;
  }

  isInitialized() {
    return this.isRegistered && this.client !== null;
  }

  // ── Event registration ───────────────────────────────────────────────────

  on(event, callback) {
    if (this.callbacks.hasOwnProperty(event)) {
      this.callbacks[event] = callback;
    }
  }

  off(event) {
    if (this.callbacks.hasOwnProperty(event)) {
      this.callbacks[event] = null;
    }
  }

  // ── Cleanup ──────────────────────────────────────────────────────────────

  async dispose() {
    if (this.currentSession) {
      try { await this.hangup(); } catch (e) { /* ignore */ }
    }

    if (this.client) {
      try { this.client.disconnect(); } catch (e) { /* ignore */ }
      this.client = null;
    }

    this.isRegistered = false;
    this.setConnectionState('disconnected');
  }
}

export const telnyxService = new TelnyxService();
export const ringCentralService = telnyxService;
export default telnyxService;
